import { BridgeComponent } from "@hotwired/hotwire-native-bridge"
import { beginLoading, endLoading, cancelTimeout } from "./social_sign_in_loading"

// Bridge pair: bridge--apple-sign-in ↔ AppleSignInComponent (iOS).
//
// Flow:
//   1. User taps the Apple button in the webview.
//   2. We immediately swap the button into a loading state and disable
//      peer auth controls (other provider, password form).
//   3. We send a "signIn" message to native; iOS presents
//      ASAuthorizationController and replies with
//      { idToken, fullName?, email? } (or { error: "canceled" | ... }).
//   4. On success we POST those values to /:locale/auth/apple via a hidden
//      form so Turbo handles the redirect + session cookie set. The
//      loading state stays on until navigation; if the POST fails we
//      restore the button via turbo:submit-end.
//   5. On native error, we restore. "canceled" is silent; everything else
//      shows a toast.
//
// Browser (no native bridge): this controller's `enabled` is false and the
// click is a no-op. Same convention as the Google bridge.
export default class extends BridgeComponent {
  static component = "apple-sign-in"
  static values = {
    endpoint: String,
    csrfToken: String,
    loadingLabel: String,
    errorMessage: String,
    canceledMessage: String
  }

  signIn(event) {
    event?.preventDefault()

    if (!this.enabled) {
      console.warn("[apple-sign-in] native bridge not enabled — ignoring click")
      return
    }
    if (this._signInLoading) return

    beginLoading(this, { label: this.loadingLabelValue || "Signing you in…" })

    this.send("signIn", {}, (message) => {
      const data = message?.data || {}

      if (data.error) {
        const isCancel = data.error === "canceled"
        endLoading(this, {
          error: !isCancel,
          errorMessage: isCancel ? null : (this.errorMessageValue || "Sign-in failed. Please try again.")
        })
        if (!isCancel) {
          console.warn("[apple-sign-in] native returned error:", data.error)
        }
        return
      }

      if (!data.idToken) {
        endLoading(this, {
          error: true,
          errorMessage: this.errorMessageValue || "Sign-in failed. Please try again."
        })
        console.warn("[apple-sign-in] missing idToken in reply")
        return
      }

      this.submitToken(data)
    })
  }

  submitToken({ idToken, rawNonce, fullName, email }) {
    const form = document.createElement("form")
    form.method = "POST"
    form.action = this.endpointValue
    form.hidden = true
    form.appendChild(this.hiddenField("authenticity_token", this.csrfTokenValue))
    form.appendChild(this.hiddenField("id_token", idToken))
    if (rawNonce) form.appendChild(this.hiddenField("raw_nonce", rawNonce))
    if (fullName) form.appendChild(this.hiddenField("full_name", fullName))
    if (email)    form.appendChild(this.hiddenField("email", email))

    // When this button is rendered on the onboarding auth step, the
    // anonymous-flow answers are stamped on window.__onboardingPayload by
    // the onboarding controller. Forwarding them here lets the server
    // create the User + UserSetting + Budget in one transaction.
    const onboardingPayload = (typeof window !== "undefined") ? window.__onboardingPayload : null
    if (onboardingPayload) {
      form.appendChild(this.hiddenField("onboarding_payload", JSON.stringify(onboardingPayload)))
    }

    form.addEventListener("turbo:submit-end", (event) => {
      if (event.detail?.success) {
        cancelTimeout(this)
      } else {
        endLoading(this, {
          error: true,
          errorMessage: this.errorMessageValue || "Sign-in failed. Please try again."
        })
      }
    }, { once: true })

    document.body.appendChild(form)
    form.requestSubmit()
  }

  disconnect() {
    cancelTimeout(this)
    super.disconnect?.()
  }

  hiddenField(name, value) {
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = name
    input.value = value
    return input
  }
}
